import React, { useEffect, useState } from 'react';
import { recurringExpensesAPI, treasuryAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  CalendarClock, CheckCircle2, Circle, Loader2, Pencil, Trash2, Repeat,
  PauseCircle, PlayCircle, AlertTriangle, Wallet,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtEur = (v) => (v == null ? '—' : `${Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const monthLabel = (ym) => {
  if (!ym) return '—';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
};
const PERIODICITIES = [
  { id: 'mensual', label: 'Mensual' },
  { id: 'trimestral', label: 'Trimestral' },
  { id: 'anual', label: 'Anual' },
];

export const ExpenseForecastSection = ({ refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [rules, setRules] = useState([]);
  const [treasury, setTreasury] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editRule, setEditRule] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', periodicity: 'mensual', charge_day: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [fc, rl] = await Promise.all([
        recurringExpensesAPI.forecast(3),
        recurringExpensesAPI.list(),
      ]);
      setData(fc);
      setRules(rl.rules || []);
      treasuryAPI.forecast().then(setTreasury).catch(() => setTreasury(null));
    } catch (e) {
      toast.error(e.message || 'No se pudo cargar la previsión');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveEdit = async () => {
    try {
      const updated = await recurringExpensesAPI.update(editRule.id, {
        amount: parseFloat(editForm.amount) || 0,
        periodicity: editForm.periodicity,
        charge_day: parseInt(editForm.charge_day, 10) || 1,
      });
      setRules((prev) => prev.map((r) => (r.id === editRule.id ? { ...r, ...updated } : r)));
      setEditRule(null);
      toast.success('Regla actualizada');
      load();
    } catch (e) { toast.error(e.message || 'No se pudo guardar'); }
  };

  const toggleActive = async (rule) => {
    try {
      await recurringExpensesAPI.update(rule.id, { active: !rule.active });
      setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, active: !rule.active } : r)));
      load();
    } catch (e) { toast.error(e.message); }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`¿Eliminar la regla "${rule.label}"?`)) return;
    try {
      await recurringExpensesAPI.remove(rule.id);
      setRules((prev) => prev.filter((r) => r.id !== rule.id));
      load();
      toast.success('Regla eliminada');
    } catch (e) { toast.error(e.message); }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }
  if (!data) return null;

  const [currentMonth, ...futureMonths] = data.months || [];
  const history = data.history || [];
  const maxHist = Math.max(1, ...history.map((h) => h.total));

  return (
    <div className="space-y-6" data-testid="expense-forecast-section">
      {!data.bank_ok && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="py-3 text-sm text-amber-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" /> No se pudieron leer los movimientos del banco ahora mismo; la previsión se muestra sin el estado de cargos.
          </CardContent>
        </Card>
      )}

      {/* Previsión de tesorería */}
      {treasury && (
        <Card className="border-primary/30" data-testid="treasury-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Wallet className="w-4 h-4 text-primary" /> Previsión de tesorería · <span className="capitalize">{monthLabel(treasury.next_month)}</span>
            </CardTitle>
            <CardDescription>Saldo actual + cobros pendientes de Quipu (venc. ≤ fin de {monthLabel(treasury.next_month)}) − gastos recurrentes previstos</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div data-testid="treasury-balance">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Saldo actual</p>
                <p className="text-xl font-bold mt-0.5">{treasury.balance_now != null ? fmtEur(treasury.balance_now) : '—'}</p>
              </div>
              <div data-testid="treasury-income">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Cobros esperados</p>
                <p className="text-xl font-bold text-emerald-600 mt-0.5">+{fmtEur(treasury.pending_income_due_next)}</p>
                <p className="text-[10px] text-muted-foreground">{fmtEur(treasury.pending_income_total)} pendiente en total ({(treasury.pending_invoices || []).length} facturas)</p>
              </div>
              <div data-testid="treasury-expenses">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Gastos previstos</p>
                <p className="text-xl font-bold text-red-600 mt-0.5">-{fmtEur(treasury.expected_expenses_next_month)}</p>
                <p className="text-[10px] text-muted-foreground">recurrentes configurados</p>
              </div>
              <div data-testid="treasury-projected">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Saldo estimado</p>
                <p className={`text-xl font-bold mt-0.5 ${treasury.projected_balance != null && treasury.projected_balance < 0 ? 'text-red-600' : 'text-primary'}`}>
                  {treasury.projected_balance != null ? fmtEur(treasury.projected_balance) : '—'}
                </p>
              </div>
            </div>
            {(!treasury.quipu_ok || !treasury.bank_ok) && (
              <p className="text-[11px] text-amber-700 mt-3 flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3" /> {!treasury.quipu_ok ? 'Quipu no disponible ahora mismo (cobros vacíos).' : ''} {!treasury.bank_ok ? 'Banco no disponible (saldo vacío).' : ''}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Previsión próximos meses */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {(data.months || []).map((m, idx) => (
          <Card key={m.month} className={idx === 0 ? 'border-primary/40' : ''} data-testid={`forecast-month-${m.month}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 capitalize"><CalendarClock className="w-4 h-4 text-primary" /> {monthLabel(m.month)}</span>
                <span className="text-lg font-bold text-red-600" data-testid={`forecast-total-${m.month}`}>{fmtEur(m.total)}</span>
              </CardTitle>
              <CardDescription>{idx === 0 ? 'Mes actual — ✓ si el cargo ya llegó al banco' : 'Previsión de cargos recurrentes'}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1.5 max-h-64 overflow-y-auto">
              {m.items.length === 0 && (
                <p className="text-sm text-muted-foreground py-4 text-center">Sin gastos recurrentes previstos. Márcalos al categorizar un movimiento.</p>
              )}
              {m.items.map((it) => (
                <div key={it.rule_id} className="flex items-center justify-between gap-2 text-sm" data-testid={`forecast-item-${m.month}-${it.rule_id}`}>
                  <div className="flex items-center gap-1.5 min-w-0">
                    {idx === 0 ? (
                      it.charged
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        : <Circle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    ) : <Circle className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />}
                    <span className="truncate" title={it.label}>{it.label}</span>
                    {it.category && <Badge variant="outline" className="text-[9px] shrink-0 hidden sm:inline-flex">{it.category}</Badge>}
                  </div>
                  <div className="shrink-0 text-right">
                    <span className="font-medium">{fmtEur(it.amount)}</span>
                    <span className="text-[10px] text-muted-foreground ml-1.5">
                      {idx === 0 && it.charged ? (it.charged_date || '').slice(0, 10) : `~día ${it.expected_day}`}
                    </span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Fijos vs variables + comparativa */}
      <Card data-testid="expense-structure-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Repeat className="w-4 h-4 text-primary" /> Estructura de gastos · últimos 6 meses</CardTitle>
          <CardDescription>Fijo = cargos que coinciden con tus gastos recurrentes · Variable = el resto</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {history.map((h) => (
            <div key={h.month} className="space-y-1" data-testid={`structure-month-${h.month}`}>
              <div className="flex items-center justify-between text-xs">
                <span className="capitalize font-medium">{monthLabel(h.month)}</span>
                <span>
                  <span className="text-indigo-600 font-medium">{fmtEur(h.fixed)} fijo</span>
                  <span className="text-muted-foreground"> · </span>
                  <span className="text-slate-600">{fmtEur(h.variable)} variable</span>
                  <span className="text-muted-foreground"> · </span>
                  <strong>{fmtEur(h.total)}</strong>
                </span>
              </div>
              <div className="h-3 rounded-full bg-muted overflow-hidden flex" style={{ width: `${Math.max(2, (h.total / maxHist) * 100)}%` }}>
                {h.total > 0 && (
                  <>
                    <div className="bg-indigo-500 h-full" style={{ width: `${(h.fixed / h.total) * 100}%` }} />
                    <div className="bg-slate-300 h-full flex-1" />
                  </>
                )}
              </div>
            </div>
          ))}
          {history.every((h) => h.total === 0) && (
            <p className="text-sm text-muted-foreground text-center py-4">Sin movimientos de gasto en el banco en los últimos meses.</p>
          )}
        </CardContent>
      </Card>

      {/* Gestor de reglas */}
      <Card data-testid="recurring-rules-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Repeat className="w-4 h-4 text-primary" /> Gastos recurrentes configurados ({rules.length})</CardTitle>
          <CardDescription>Se crean al marcar un movimiento como recurrente en el modal de categorización. Aquí puedes ajustar importe, periodicidad o día.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {rules.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">Aún no hay gastos recurrentes. Abre un movimiento en la pestaña Transacciones y márcalo como Mensual/Trimestral/Anual.</p>
            )}
            {rules.map((r) => (
              <div key={r.id} className={`px-4 py-2.5 ${!r.active ? 'opacity-50' : ''}`} data-testid={`rule-row-${r.id}`}>
                {editRule?.id === r.id ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium flex-1 min-w-[140px] truncate">{r.label}</span>
                    <Input type="number" min="0" step="0.01" className="h-8 w-24 text-right" value={editForm.amount}
                      onChange={(e) => setEditForm((f) => ({ ...f, amount: e.target.value }))} data-testid={`rule-amount-input-${r.id}`} />
                    <select className="h-8 border rounded-md px-2 text-sm bg-background" value={editForm.periodicity}
                      onChange={(e) => setEditForm((f) => ({ ...f, periodicity: e.target.value }))} data-testid={`rule-period-select-${r.id}`}>
                      {PERIODICITIES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground">día</span>
                      <Input type="number" min="1" max="31" className="h-8 w-16 text-right" value={editForm.charge_day}
                        onChange={(e) => setEditForm((f) => ({ ...f, charge_day: e.target.value }))} data-testid={`rule-day-input-${r.id}`} />
                    </div>
                    <Button size="sm" className="h-8" onClick={saveEdit} data-testid={`rule-save-${r.id}`}>Guardar</Button>
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditRule(null)}>Cancelar</Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium truncate" title={r.label}>{r.label}</span>
                      {r.category && <Badge variant="outline" className="text-[9px] shrink-0">{r.category}</Badge>}
                      <Badge variant="outline" className="text-[9px] shrink-0 capitalize">{r.periodicity}</Badge>
                      {!r.active && <Badge className="text-[9px] bg-amber-100 text-amber-700 shrink-0">Pausada</Badge>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-sm font-semibold">{fmtEur(r.amount)}</span>
                      <span className="text-[10px] text-muted-foreground">~día {r.charge_day}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground"
                        onClick={() => { setEditRule(r); setEditForm({ amount: String(r.amount), periodicity: r.periodicity, charge_day: String(r.charge_day) }); }}
                        data-testid={`rule-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => toggleActive(r)} title={r.active ? 'Pausar' : 'Reactivar'} data-testid={`rule-toggle-${r.id}`}>
                        {r.active ? <PauseCircle className="w-3.5 h-3.5" /> : <PlayCircle className="w-3.5 h-3.5" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => deleteRule(r)} data-testid={`rule-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExpenseForecastSection;
